import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";
import { films } from "@/mocks/films";
import type { Film } from "@/types/content";

const principles = [
  {
    icon: "ri-aspect-ratio-line",
    title: "半球形银幕结构",
    desc: "银幕为直径 22 米的半球穹顶，从观众前方一直延伸到头顶，彻底消除传统平面银幕的边界感。",
  },
  {
    icon: "ri-projector-2-line",
    title: "多机无缝融合",
    desc: "六台 8K 激光投影机同步输出，通过边缘融合算法校准色彩与亮度，让整块穹顶画面如一。",
  },
  {
    icon: "ri-surround-sound-line",
    title: "12.1 声道声场",
    desc: "声源按穹顶方位精确布置，声音可从上、下、左、右任意方向抵达，实现与画面完全同步的空间定位。",
  },
];

const cases = [
  {
    image:
      "https://readdy.ai/api/search-image?query=Wide%20shot%20of%20a%20dome%20cinema%20audience%20immersed%20in%20a%20starry%20space%20projection%20above%20them%2C%20deep%20navy%20ambient%20lighting%2C%20soft%20amber%20seats%2C%20photorealistic%20interior%2C%20high%20detail%2C%20premium%20architecture%20photography&width=900&height=600&seq=qiubu-case-01&orientation=landscape",
    title: "太空科普专场",
    desc: "与天文馆合作，将星图与深空影像投射到整块穹顶，打造沉浸式宇宙课堂。",
  },
  {
    image:
      "https://readdy.ai/api/search-image?query=Wide%20artistic%20scene%20of%20colorful%20abstract%20projection%20swirling%20across%20a%20curved%20dome%20ceiling%2C%20vivid%20teal%20and%20orange%20light%2C%20dark%20auditorium%20below%2C%20high%20detail%2C%20moody%20atmospheric%20art%20installation&width=900&height=600&seq=qiubu-case-02&orientation=landscape",
    title: "沉浸艺术展演",
    desc: "为艺术家提供穹顶画布，让抽象影像与实时音画互动在整个空间流动。",
  },
  {
    image:
      "https://readdy.ai/api/search-image?query=Wide%20scene%20of%20an%20underwater%20documentary%20projection%20wrapping%20around%20a%20curved%20dome%20screen%20with%20blue%20light%20and%20fish%20silhouettes%2C%20dark%20audience%20seating%20below%2C%20high%20detail%2C%20cinematic%20immersion&width=900&height=600&seq=qiubu-case-03&orientation=landscape",
    title: "自然纪录片放映",
    desc: "深海与极地影像在穹顶之上连贯铺展，让自然探索拥有前所未有的临场感。",
  },
];

export default function Technology() {
  usePageMeta(
    "特色技术-球幕影院穹顶沉浸观影技术介绍",
    "了解球幕影院的核心技术：半球形银幕、多机融合投影与 12.1 声道空间声场，以及穹顶沉浸技术的应用案例。",
    "球幕技术,穹顶影院,沉浸观影,激光投影,空间声场",
  );
  const related = (films as Film[]).filter((film) => film.tags.includes("球幕技术")).slice(0, 4);

  return (
    <div className="w-full pt-20">
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20inside%20a%20dome%20cinema%20with%20a%20glowing%20blue%20spherical%20screen%20and%20beam%20of%20projector%20light%2C%20dark%20navy%20atmosphere%2C%20abstract%20technical%20visual%2C%20high%20detail%2C%20premium%20cinematic%20key%20visual&width=1920&height=700&seq=qiubu-tech-hero&orientation=landscape"
            alt="球幕影院穹顶投影技术现场"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/80 to-background-50" />
        </div>
        <div className="relative max-w-content mx-auto px-4 md:px-8 py-20 md:py-28">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">技术介绍</span>
          </nav>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-label bg-accent-500/15 text-accent-500 mb-5">
            <i className="ri-lightbulb-flash-line" />
            穹顶沉浸技术
          </span>
          <h1 className="font-heading font-bold text-3xl md:text-5xl text-foreground-950 leading-tight mb-5 max-w-3xl">
            让影像包裹身体，让故事走进感官
          </h1>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-2xl">
            球幕影院通过半球银幕、多机融合与空间声场三大核心技术，
            把「观看」变成「置身其中」。下面带你了解这套沉浸系统如何运作。
          </p>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-8 py-16 md:py-20">
        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="rounded-lg overflow-hidden aspect-[4/3] bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Technical%20diagram%20style%20photo%20of%20a%20hemispherical%20dome%20screen%20with%20multiple%20projector%20light%20beams%20converging%20on%20the%20surface%2C%20deep%20navy%20and%20cyan%20tone%2C%20dark%20auditorium%2C%20high%20detail%2C%20premium%20cinematic%20scenography&width=1200&height=900&seq=qiubu-tech-01&orientation=landscape"
                alt="多机融合投影在穹顶银幕上校准"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-xs font-label tracking-widest text-accent-500 uppercase mb-3">Principle</p>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-5">
                技术原理：三件事，让沉浸成立
              </h2>
              <div className="flex flex-col gap-5">
                {principles.map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-xl shrink-0">
                      <i className={item.icon} />
                    </span>
                    <div>
                      <h3 className="font-heading font-semibold text-foreground-950 mb-1.5">{item.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="bg-background-100/60 border-y border-background-200/60">
        <div className="max-w-content mx-auto px-4 md:px-8 py-16 md:py-20">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <p className="text-xs font-label tracking-widest text-accent-500 uppercase mb-3">Advantage</p>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-5">
                  视觉优势：没有边框的世界
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  传统影厅中，观众的视线始终被银幕边框切割。穹顶结构让画面延伸到视野边缘之外，
                  人眼无法一次看全所有内容，只能主动转动头部去探索——这正是沉浸感的来源。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  配合高动态范围激光投影与深黑位表现，暗部星空与高光明焰能同时保留细节，
                  画面在整块穹顶上保持色彩一致，长时间观看也不易疲劳。
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                    <p className="font-heading font-bold text-2xl text-accent-500">0.1%</p>
                    <p className="text-xs text-foreground-600 mt-1">边缘融合误差</p>
                  </div>
                  <div className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                    <p className="font-heading font-bold text-2xl text-accent-500">360°</p>
                    <p className="text-xs text-foreground-600 mt-1">水平覆盖视角</p>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2 rounded-lg overflow-hidden aspect-[4/3] bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Wide%20artistic%20photo%20of%20a%20colorful%20aurora%20projection%20wrapping%20fully%20around%20a%20curved%20dome%20ceiling%20above%20dark%20theater%20seats%2C%20vivid%20green%20and%20orange%20light%2C%20deep%20navy%20tone%2C%20high%20detail%2C%20premium%20immersive%20scenography&width=1200&height=900&seq=qiubu-tech-02&orientation=landscape"
                  alt="穹顶之上环绕的极光影像"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="mb-10">
          <p className="text-xs font-label tracking-widest text-accent-500 uppercase mb-3">Cases</p>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">技术应用案例</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {cases.map((item, i) => (
            <Reveal key={item.title} delay={i * 90}>
              <article className="h-full rounded-lg overflow-hidden bg-background-100 border border-background-200/70 flex flex-col">
                <div className="aspect-[3/2] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-background-100/60 border-t border-background-200/60">
        <div className="max-w-content mx-auto px-4 md:px-8 py-16 md:py-20">
          <div className="flex items-end justify-between gap-4 mb-8">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">相关影片推荐</h2>
            <Link
              to="/films"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-label text-foreground-700 hover:text-accent-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看全部
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {related.map((film) => (
              <Link
                key={film.id}
                to={`/films/${film.id}`}
                className="group flex items-center gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-accent-500/60 transition-colors cursor-pointer"
              >
                <div className="w-14 h-20 rounded-md overflow-hidden bg-background-200 shrink-0">
                  <img
                    src={film.poster}
                    alt={film.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-accent-500 transition-colors">
                    {film.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{film.genres[0]} · {film.year}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}